import { supabase } from './supabaseClient';

export const PARTNER_DOC_BUCKET = 'partner-application-docs';

// Keys match what ERP's Applications queue expects in `documents` — required
// ones (per the business's KYC checklist) vs optional ones.
export const REQUIRED_DOC_KEYS = ['drug_license', 'tin', 'nid', 'photo'] as const;
export const OPTIONAL_DOC_KEYS = ['bin', 'bank_solvency', 'bank_statement', 'agreement'] as const;
export type PartnerDocKey = (typeof REQUIRED_DOC_KEYS)[number] | (typeof OPTIONAL_DOC_KEYS)[number];

export const DOC_LABELS: Record<PartnerDocKey, string> = {
  drug_license: 'Drug License',
  tin: 'TIN Certificate',
  nid: 'NID',
  photo: "Applicant's Photo",
  bin: 'BIN Certificate (optional)',
  bank_solvency: 'Bank Solvency Certificate (optional)',
  bank_statement: '6-Month Bank Statement (optional)',
  agreement: 'Signed Agreement (optional)',
};

export type PartnerDocFiles = Partial<Record<PartnerDocKey, File | null>>;

// Pairs a doc's number field (when it has one) with its file upload —
// drug license/TIN/NID/BIN all carry a number, bank solvency/statement/
// photo don't. Shared by the Add Reseller and application detail pages.
export const DOC_NUMBER_FIELDS: Partial<Record<PartnerDocKey, { key: 'drug_license_no' | 'tin_no' | 'nid_no' | 'bin_no'; label: string }>> = {
  drug_license: { key: 'drug_license_no', label: 'Drug License Number' },
  tin: { key: 'tin_no', label: 'TIN Number' },
  nid: { key: 'nid_no', label: 'NID Number' },
  bin: { key: 'bin_no', label: 'BIN Number' },
};

export interface PartnerApplicationFields {
  business_name: string;
  business_type: string | null;
  contact_person: string;
  designation?: string | null;
  email: string;
  phone: string;
  city?: string | null;
  address?: string | null;
  map_link?: string | null;
  monthly_volume?: string | null;
  why_partner?: string | null;
  drug_license_no: string;
  tin_no: string;
  nid_no: string;
  bin_no?: string | null;
  agreement_terms?: string | null;
  agreement_start_date?: string | null;
  agreement_end_date?: string | null;
  advance_payment_pct?: number | null;
  first_order_advance_pct?: number | null;
}

// Uploads whatever files are present to a per-submission folder, then
// inserts the application row with the resulting storage paths. Used by
// both the public /partner-apply page and rxk admin's internal Add
// Reseller form, so ERP's review queue behaves identically either way.
export async function submitPartnerApplication(
  fields: PartnerApplicationFields,
  files: PartnerDocFiles,
): Promise<{ error: string | null }> {
  const uploadId = crypto.randomUUID();
  const documents: Partial<Record<PartnerDocKey, string>> = {};

  for (const key of [...REQUIRED_DOC_KEYS, ...OPTIONAL_DOC_KEYS]) {
    const file = files[key];
    if (!file) continue;
    const ext = file.name.split('.').pop() || 'bin';
    const path = `${uploadId}/${key}.${ext}`;
    const { error: uploadErr } = await supabase.storage.from(PARTNER_DOC_BUCKET).upload(path, file);
    if (uploadErr) return { error: `Failed to upload ${DOC_LABELS[key]}: ${uploadErr.message}` };
    documents[key] = path;
  }

  // Goes through an edge function (service-role insert + confirmation/admin-
  // alert notifications) rather than a direct table insert — the table has
  // no anon insert policy anymore, matching how core_orders is only ever
  // written to via its own edge function.
  const { data, error } = await supabase.functions.invoke<{ error?: string }>('core-submit-partner-application', {
    body: { ...fields, documents },
  });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return { error: null };
}
